/* eslint-disable react/prop-types */
import React, { useState, useEffect, useContext } from "react";
//Packages
import axios from "axios";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { isAuthenticated } from "./services/auth"; //import auth
//Context
import { BusinessContextConsumer } from "../BusinessContext";
//Subcomponents
import StatusBlock from "../common/StatusBlock";
import FadeIn from "../common/FadeIn";
//Images and Icons
import { MdSave, MdOutlineCancel, MdOutlineUpgrade } from "react-icons/md";
// Colour Picker
import { BlockPicker } from "react-color";

const Configure = () => {
  // Handle Context
  const BusinessContext = useContext(BusinessContextConsumer);
  const { connectedAccountID, planType } = BusinessContext;
  // State
  const [gtmTag, setGtmTag] = useState("");
  const [terms, setTerms] = useState("");
  const [privacyPolicy, setPrivacyPolicy] = useState("");
  const [tradeName, setTradeName] = useState("");
  const [stripeTaxRate, setStripeTaxRate] = useState("");
  const [logo, setLogo] = useState("");
  const [brandColour, setBrandColour] = useState("");
  const [generalTheme, setGeneralTheme] = useState("");
  // Manage View
  const [viewMode, setViewMode] = useState("");
  // Handle Clicks, Submits, Errors
  const [submitClicked, setSubmitClicked] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Get the Initial States and Details
  useEffect(async () => {
    const userDetailsRequest = await axios.get(`/api/admin/details/options_set`, {
      withCredentials: true,
    });
    setBrandColour(userDetailsRequest.data.brandColour);
    setGeneralTheme(userDetailsRequest.data.generalTheme);
    setGtmTag(userDetailsRequest.data.gtmTag);
    setTerms(userDetailsRequest.data.terms);
    setPrivacyPolicy(userDetailsRequest.data.privacyPolicy);
    setTradeName(userDetailsRequest.data.tradeName);
    setStripeTaxRate(userDetailsRequest.data.stripeTaxRate);
    return setLogo(userDetailsRequest.data.logo);
  }, []);

  // Dismiss Message
  const dismissMessages = () => {
    setSuccessMessage("");
    return setErrorMessage("");
  };

  return (
    <FadeIn delay={250} duration={450}>
      {isAuthenticated() && (
        <Container>
          <Row>
            <Col className='text-center mt-4 mb-4'>
              <h3>Configure Your Instance</h3>
              <div>
                <StatusBlock
                  errorMessage={errorMessage}
                  successMessage={successMessage}
                  dismissMessages={dismissMessages}
                />
              </div>
              <div>
                <ModeSelect viewMode={viewMode} setViewMode={setViewMode} />
              </div>
            </Col>
          </Row>
          {viewMode === "tradeName" && (
            <TradeNameForm
              tradeName={tradeName}
              setTradeName={setTradeName}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              setSubmitClicked={setSubmitClicked}
              submitClicked={submitClicked}
            />
          )}
          {viewMode === "logo" && (
            <LogoForm
              logo={logo}
              setLogo={setLogo}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              setSubmitClicked={setSubmitClicked}
              submitClicked={submitClicked}
            />
          )}
          {viewMode === "gtmTag" && (
            <GTMTagForm
              planType={planType}
              gtmTag={gtmTag}
              setGtmTag={setGtmTag}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              setSubmitClicked={setSubmitClicked}
              submitClicked={submitClicked}
            />
          )}
          {viewMode === "stripeTaxRate" && (
            <StripeTaxRateForm
              connectedAccountID={connectedAccountID}
              stripeTaxRate={stripeTaxRate}
              setStripeTaxRate={setStripeTaxRate}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              setSubmitClicked={setSubmitClicked}
              submitClicked={submitClicked}
            />
          )}
          {viewMode === "terms" && (
            <TermsAndConditionsForm
              terms={terms}
              setTerms={setTerms}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              setSubmitClicked={setSubmitClicked}
              submitClicked={submitClicked}
            />
          )}
          {viewMode === "privacyPolicy" && (
            <PrivacyPolicyForm
              privacyPolicy={privacyPolicy}
              setPrivacyPolicy={setPrivacyPolicy}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              setSubmitClicked={setSubmitClicked}
              submitClicked={submitClicked}
            />
          )}
          {viewMode === "branding" && (
            <Branding
              brandColour={brandColour}
              setBrandColour={setBrandColour}
              generalTheme={generalTheme}
              setGeneralTheme={setGeneralTheme}
              setSuccessMessage={setSuccessMessage}
              setSubmitClicked={setSubmitClicked}
              submitClicked={submitClicked}
            />
          )}
        </Container>
      )}
    </FadeIn>
  );
};

const ModeSelect = (props) => {
  return (
    <Row>
      <Col>
        <Form className='Venue mt-3'>
          <Form.Label>Choose Setting</Form.Label>
          <Form.Control
            as='select'
            value={props.viewMode}
            onChange={(e) => props.setViewMode(e.target.value)}
            custom
            className='border'
          >
            <option value='' disabled defaultValue>
              Select Option
            </option>
            <option value='tradeName'>Trade Name</option>
            <option value='logo'>Logo</option>
            <option value='terms'>Terms &amp; Conditions</option>
            <option value='privacyPolicy'>Privacy Policy</option>
            <option value='stripeTaxRate'>Tax Rate</option>
            <option value='gtmTag'>Google Tag Manager</option>
            <option value='branding'>Colour Scheme</option>
          </Form.Control>
        </Form>
      </Col>
    </Row>
  );
};

const TradeNameForm = (props) => {
  // Request Change
  const changeTradeName = async (e) => {
    e.preventDefault();
    // Data
    const data = {
      tradeName: props.tradeName,
    };
    // Set the Click State
    props.setSubmitClicked(true);
    // Request the Change
    const requestTradeNameChange = await axios.put("/api/admin/details/tradename/", data, {
      withCredentials: true,
    });

    if (requestTradeNameChange.status === 200) {
      props.setSuccessMessage("Trade Name successfully set.");
      return window.location.reload(true);
    } else {
      props.setSubmitClicked(false);
      return props.setErrorMessage(
        "There was an error and we could not set your tradename. Please contact support if the problem persists."
      );
    }
  };

  return (
    <FadeIn delay={250} duration={450}>
      <Row className='shadow mt-4 p-3 border'>
        <Col>
          <Form onSubmit={changeTradeName}>
            <Row>
              <Col className='mt-2'>
                <Form.Group controlId='TradeNameForm'>
                  <Form.Label>
                    <h3>Trade Name</h3>
                  </Form.Label>
                  <Form.Control
                    type='text'
                    maxLength='64'
                    value={props.tradeName}
                    onChange={(e) => props.setTradeName(e.target.value)}
                    required
                    className='border'
                  />
                  <Form.Text className='text-muted'>
                    The name you advertise your business with can be set here, rather than your
                    legal business name. It will display if you don&apos;t set a logo.
                  </Form.Text>
                </Form.Group>
                <Button variant='primary' type='submit' disabled={props.submitClicked}>
                  <MdSave /> Save
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </FadeIn>
  );
};

const LogoForm = (props) => {
  const logo = props.logo;

  const changeLogo = (e) => {
    let image = e.target.files[0];
    let form = new FormData();
    form.append("logo", image);
    props.setLogo(form);
  };

  const removeLogo = async (e) => {
    e.preventDefault();
    // Set submit clicked
    props.setSubmitClicked(true);
    // Request the logo remove
    const removeLogoRequest = await axios.delete("/api/admin/details/logo", {
      withCredentials: true,
    });
    // Handle response
    if (removeLogoRequest.status === 200) {
      props.setSuccessMessage("Logo successfully removed - the app will now reload.");
      return window.location.reload(true);
    } else {
      props.setSubmitClicked(false);
      return props.setErrorMessage(
        "There was an error and we could not unset your logo. Please contact support if the problem persists."
      );
    }
  };

  const addLogo = async (e) => {
    e.preventDefault();
    // Set submit clicked
    props.setSubmitClicked(true);
    // Request Logo Add
    const addLogoRequest = await axios.put("/api/admin/details/logo", logo, {
      withCredentials: true,
    });
    // Handle response
    if (addLogoRequest.status === 200) {
      props.setSuccessMessage("Logo successfully set - the app will now reload.");
      return window.location.reload(true);
    } else {
      props.setSubmitClicked(false);
      return props.setErrorMessage(
        "There was an error and we could not set logo. Please limit logos to 512kb and ensure they are in .png format."
      );
    }
  };

  return (
    <FadeIn delay={250} duration={450}>
      <Row className='shadow mt-4 p-3 border'>
        <Col>
          <Form onSubmit={addLogo}>
            <Row>
              <Col className='mt-2'>
                <Form.Group controlId='formFile'>
                  <Form.Label>
                    <h3>Logo</h3>
                  </Form.Label>
                  {logo.length >= 100 && (
                    <p>
                      <img
                        src={`data:image/png;base64,${logo}`}
                        className='mt-3 mb-3 display-logo'
                        alt='logo'
                      />
                    </p>
                  )}
                  <Form.Control
                    type='file'
                    name='logo'
                    onChange={changeLogo}
                    required
                    className='border'
                  />
                  <Form.Text className='text-muted'>
                    Logos should be in .png format. They work best as roundels with transparent
                    backgrounds or straplines. Max file size is 512kb.
                  </Form.Text>
                </Form.Group>
                <Button variant='primary' type='submit'>
                  <MdSave /> Save
                </Button>
                {logo.length >= 100 && (
                  <Button
                    variant='danger'
                    onClick={removeLogo}
                    className='ms-3'
                    disabled={props.submitClicked}
                  >
                    <MdOutlineCancel /> Remove
                  </Button>
                )}
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </FadeIn>
  );
};

const GTMTagForm = (props) => {
  // Request Change
  const changeGTMTag = async (e) => {
    e.preventDefault();
    // Data
    const data = {
      gtmTag: props.gtmTag,
    };
    // Set the Click State
    props.setSubmitClicked(true);
    // Request the Change
    const requestGTMtagChange = await axios.put("/api/admin/details/gtm/", data, {
      withCredentials: true,
    });

    if (requestGTMtagChange.status === 200) {
      props.setSubmitClicked(false);
      return props.setSuccessMessage("GTM Tag Successfully Set");
    } else {
      props.setSubmitClicked(false);
      return props.setErrorMessage(
        "There was an error and we could not set your GTM tag. Please contact support if the problem persists."
      );
    }
  };

  return (
    <FadeIn delay={250} duration={450}>
      <Row className='shadow mt-4 p-3 border'>
        <Col>
          <Form onSubmit={changeGTMTag}>
            <Row>
              <Col className='mt-2'>
                <Form.Group controlId='GTMForm'>
                  <Form.Label>
                    <h3>GTM Tag</h3>
                  </Form.Label>
                  <Form.Control
                    type='text'
                    maxLength='11'
                    value={props.gtmTag}
                    onChange={(e) => props.setGtmTag(e.target.value)}
                    className='border'
                  />
                  <Form.Text className='text-muted'>
                    You can get guidance on{" "}
                    <a href='https://support.google.com/tagmanager/answer/6103696?hl=en'>
                      how to set up Google Tag Manager here
                    </a>{" "}
                    or contact our support who will be happy to help .
                  </Form.Text>
                </Form.Group>
                {props.planType >= 2 ? (
                  <Button variant='primary' type='submit' disabled={props.submitClicked}>
                    <MdSave /> Save
                  </Button>
                ) : (
                  <Button variant='warning' href='https://signaclass.com/account'>
                    <MdOutlineUpgrade /> Upgrade Plan
                  </Button>
                )}
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </FadeIn>
  );
};

const StripeTaxRateForm = (props) => {
  // Request Change
  const changeTaxRate = async (e) => {
    e.preventDefault();
    // Data
    const data = {
      stripeTaxRate: props.stripeTaxRate,
      connectedAccountID: props.connectedAccountID,
    };
    // Set the Click State
    props.setSubmitClicked(true);
    // Request the Change
    const requestTaxRateChange = await axios.put("/api/admin/details/tax_rate/", data, {
      withCredentials: true,
    });

    if (requestTaxRateChange.status === 200) {
      props.setSubmitClicked(false);
      return props.setSuccessMessage("Tax Rate Successfully Set");
    } else {
      props.setSubmitClicked(false);
      return props.setErrorMessage(
        "There was an error and we could not set your tax rate. Please ensure it is set up correctly in stripe and copy and paste the ID exactly."
      );
    }
  };

  return (
    <FadeIn delay={250} duration={450}>
      <Row className='shadow mt-4 p-3 border'>
        <Col>
          <Form onSubmit={changeTaxRate}>
            <Row>
              <Col className='mt-2'>
                <Form.Group controlId='stripeTaxRate'>
                  <Form.Label>
                    <h3>Tax Rate</h3>
                  </Form.Label>
                  <Form.Control
                    type='text'
                    maxLength='256'
                    value={props.stripeTaxRate}
                    onChange={(e) => props.setStripeTaxRate(e.target.value)}
                    className='border'
                  />
                  <Form.Text className='text-muted'>
                    You can set up one tax rate (e.g VAT or Sales Tax) in stripe which will be saved
                    and used for all future payments.{" "}
                    <strong>
                      Any changes will not affect existing payments tax rates and you will need to
                      adjust them.
                    </strong>{" "}
                    To set up a stripe tax rate go to{" "}
                    <a href='https://dashboard.stripe.com/tax-rates'>
                      https://dashboard.stripe.com/tax-rates
                    </a>{" "}
                    and copy paste the tax rate ID here. Signaclass will only display prices
                    including taxes.
                  </Form.Text>
                </Form.Group>
                <Button variant='primary' type='submit' disabled={props.submitClicked}>
                  <MdSave /> Save
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </FadeIn>
  );
};

const TermsAndConditionsForm = (props) => {
  // Request Change
  const changeTerms = async (e) => {
    e.preventDefault();
    // Data
    const data = {
      terms: props.terms,
    };
    // Set the Click State
    props.setSubmitClicked(true);
    // Request the Change
    const requestTermsChange = await axios.put("/api/admin/details/terms/", data, {
      withCredentials: true,
    });

    if (requestTermsChange.status === 200) {
      props.setSubmitClicked(false);
      return props.setSuccessMessage("Terms successfully set.");
    } else {
      props.setSubmitClicked(false);
      return props.setErrorMessage(
        "There was an error and we could not set your terms and conditions. Please contact support if the problem persists."
      );
    }
  };

  return (
    <FadeIn delay={250} duration={450}>
      <Row className='shadow mt-4 p-3 border'>
        <Col>
          <Form onSubmit={changeTerms}>
            <Row>
              <Col className='mt-2'>
                <Form.Group controlId='TermsForm'>
                  <Form.Label>
                    <h3>Terms and Conditions</h3>
                  </Form.Label>
                  <Form.Control
                    as='textarea'
                    type='text'
                    maxLength='25000'
                    value={props.terms}
                    onChange={(e) => props.setTerms(e.target.value)}
                    className='border'
                  />
                  <Form.Text className='text-muted'>
                    Enter your terms and conditions here. Up to 25,000 characters. You can use{" "}
                    <strong>b, p, i, em, strong, br, h1, h2, h3, h4, h5, h6, small</strong> html tags
                    for formatting.
                  </Form.Text>
                </Form.Group>
                <Button variant='primary' type='submit' disabled={props.submitClicked}>
                  <MdSave /> Save
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </FadeIn>
  );
};

const PrivacyPolicyForm = (props) => {
  // Request Change
  const changePrivacyPolicy = async (e) => {
    e.preventDefault();
    // Data
    const data = {
      privacyPolicy: props.privacyPolicy,
    };
    // Set the Click State
    props.setSubmitClicked(true);
    // Request the Change
    const requestTermsChange = await axios.put("/api/admin/details/privacy/", data, {
      withCredentials: true,
    });

    if (requestTermsChange.status === 200) {
      props.setSubmitClicked(false);
      return props.setSuccessMessage("Privacy Policy successfully set.");
    } else {
      props.setSubmitClicked(false);
      return props.setErrorMessage(
        "There was an error and we could not set your privacy policy. Please contact support if the problem persists."
      );
    }
  };

  return (
    <FadeIn delay={250} duration={450}>
      <Row className='shadow mt-4 p-3 border'>
        <Col>
          <Form onSubmit={changePrivacyPolicy}>
            <Row>
              <Col className='mt-2'>
                <Form.Group controlId='PrivacyForm'>
                  <Form.Label>
                    <h3>Privacy Policy</h3>
                  </Form.Label>
                  <Form.Control
                    as='textarea'
                    type='text'
                    maxLength='25000'
                    value={props.privacyPolicy}
                    onChange={(e) => props.setPrivacyPolicy(e.target.value)}
                    className='border'
                  />
                  <Form.Text className='text-muted'>
                    Enter your privacy policy here. Up to 25,000 characters. You can use b, p,
                    <strong>i, em, strong, br, h1, h2, h3, h4, h5, h6, small</strong> html tags for
                    formatting.
                  </Form.Text>
                </Form.Group>
                <Button variant='primary' type='submit' disabled={props.submitClicked}>
                  <MdSave /> Save
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </FadeIn>
  );
};

const Branding = (props) => {
  const [brandColour, setBrandColour] = useState(props.brandColour);

  const colourChange = (color, event) => {
    event.preventDefault();
    return setBrandColour(color.hex);
  };
  // Request Change
  const changeBranding = async (e, generalTheme) => {
    e.preventDefault();
    // Data
    const data = {
      brandColour: brandColour,
      generalTheme: generalTheme,
    };
    // Set the Click State
    props.setSubmitClicked(true);
    // Request the Change
    const brandingChange = await axios.put("/api/admin/details/branding/", data, {
      withCredentials: true,
    });

    if (brandingChange.status === 200) {
      props.setSubmitClicked(false);
      return window.location.reload(true);
    } else {
      props.setSubmitClicked(false);
      return props.setErrorMessage(
        "There was an error and we could not set your branding colours. Please contact support if the problem persists."
      );
    }
  };

  return (
    <FadeIn delay={250} duration={450}>
      <Row className='shadow mt-4 p-3 border'>
        <Col>
          <div className='mt-3'>
            <h3 className="mb-3">Colour Scheme</h3>
            <BlockPicker color={brandColour} onChangeComplete={colourChange} />
            <h5 className="mt-3">Choose Tone</h5>
            <p>
              Save based on whether this is a dark or light colour tone for best result. These options
              determine if navigation and footer links are dark or light; e.g if you have a strong,
              dark brand colour, save with the dark option.
            </p>
            <Button
              variant='light'
              value='light'
              type='button'
              onClick={(e) => changeBranding(e, e.target.value)}
            >
              <MdSave /> Light
            </Button>
            <br />
            <Button
              variant='dark'
              value='dark'
              type='button'
              onClick={(e) => changeBranding(e, e.target.value)}
              className='mt-2'
            >
              <MdSave /> Dark
            </Button>
          </div>
        </Col>
      </Row>
    </FadeIn>
  );
};

export default Configure;
